import { useIntl } from "react-intl";
import { graphql, useFragment } from "react-relay";
import { LocationContextValue, useLocation } from "../utils/location";
import { getOgImageUrl, getTopicPath, PathSegment } from "../utils/routing";
import { MetaLayout, MetaLayoutProps } from "../common/MetaLayout";
import { JsonObject, JsonValue, SafeScript } from "./SafeScript";
import {
  TopicHelmetFragment$data,
  TopicHelmetFragment$key,
} from "./__generated__/TopicHelmetFragment.graphql";

const Fragment = graphql`
  fragment TopicHelmetFragment on Topic {
    id
    title
    description
    createdAt
    votes
    author {
      username
      displayName
    }
    forum {
      title
      slug
      owner {
        slug
        forumOwnerKind
      }
    }
    lastComments: comments(last: 10) {
      nodes {
        content
        createdAt
        votes
        author {
          username
          displayName
        }
      }
    }
  }
`;

export interface TopicHelmetProps extends Pick<
  MetaLayoutProps,
  "metaTitle" | "metaDescription" | "metaImageAlt" | "metaImageUrl"
> {
  topic: TopicHelmetFragment$key;
  extraRichContent?: JsonObject;
  parent?: PathSegment | undefined;
}

export default function TopicHelmet(props: TopicHelmetProps) {
  const intl = useIntl();
  const topic = useFragment(Fragment, props.topic);
  const location = useLocation();
  const forumTitle = topic.forum?.title ?? undefined;

  const metaTitle = forumTitle
    ? intl.formatMessage(
        { defaultMessage: "{topicTitle} | {forumTitle}" },
        { topicTitle: topic.title, forumTitle },
      )
    : topic.title;
  return (
    <MetaLayout
      metaTitle={props.metaTitle ?? metaTitle}
      metaDescription={props.metaDescription ?? topic.description}
      metaImageUrl={
        props.metaImageUrl ??
        getOgImageUrl({ type: "topic", id: topic.id }, location.origin)
      }
      metaImageAlt={props.metaImageAlt ?? topic.title}
    >
      <meta
        name="twitter:label1"
        content={intl.formatMessage({ defaultMessage: "Written by" })}
      />
      <meta name="twitter:data1" content={topic.author.displayName} />
      <meta
        name="twitter:label2"
        content={intl.formatMessage({ defaultMessage: "Votes" })}
      />
      <meta name="twitter:data2" content={topic.votes.toString()} />
      <SafeScript type="application/ld+json">
        {formatDiscussionForumPosting(
          topic,
          location,
          props.extraRichContent,
          props.parent,
        )}
      </SafeScript>
    </MetaLayout>
  );
}

function getEntityProfilePath(username: string) {
  return `/${username}`;
}

function formatDiscussionForumPosting(
  topic: TopicHelmetFragment$data,
  location: LocationContextValue,
  extraRichContent?: JsonObject,
  parent?: PathSegment | undefined,
): JsonValue {
  const topicUrl = new URL(
    getTopicPath({
      ...topic,
      forum: {
        ...topic.forum,
        owner: {
          ...topic.forum.owner,
          parent,
        },
      },
    }),
    location.origin,
  ).toString();

  return {
    "@context": "https://schema.org",
    "@type": "DiscussionForumPosting",
    url: topicUrl,
    mainEntityOfPage: topicUrl,
    headline: topic.title,
    text: topic.description,
    author: {
      "@type": "Person",
      name: topic.author.displayName,
      url: new URL(
        getEntityProfilePath(topic.author.username),
        location.origin,
      ).toString(),
    },
    publisher: {
      "@type": "Person",
      name: topic.author.displayName,
    },
    datePublished: topic.createdAt,
    interactionStatistic: {
      "@type": "InteractionCounter",
      interactionType: "https://schema.org/LikeAction",
      userInteractionCount: topic.votes,
    },
    comment: topic.lastComments.nodes.map((comment) => ({
      "@type": "Comment",
      text: comment.content,
      author: {
        "@type": "Person",
        name: comment.author.displayName,
        url: new URL(
          getEntityProfilePath(topic.author.username),
          location.origin,
        ).toString(),
      },
      datePublished: comment.createdAt,
      interactionStatistic: {
        "@type": "InteractionCounter",
        interactionType: "https://schema.org/LikeAction",
        userInteractionCount: comment.votes,
      },
    })),
    ...extraRichContent,
  };
}
